import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFlight } from '../contexts/FlightContext';

const MyAccountScreen: React.FC = () => {
    const { user, changeUserPassword, generateTwoFactorSecret, enableTwoFactor, disableTwoFactor } = useAuth();
    const { showNotification } = useFlight();

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
    const [showPasswords, setShowPasswords] = useState(false);

    // 2FA state
    const [twoFactorStep, setTwoFactorStep] = useState<'idle' | 'setup' | 'enabled'>('idle');
    const [qrCodeUrl, setQrCodeUrl] = useState('');
    const [secretKey, setSecretKey] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
    const [is2faLoading, setIs2faLoading] = useState(false);
    const [disablePassword, setDisablePassword] = useState('');

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword.length < 6) {
            showNotification('New password must be at least 6 characters long.', 'error');
            return;
        }
        if (newPassword !== confirmNewPassword) {
            showNotification('New passwords do not match.', 'error');
            return;
        }
        setIsPasswordUpdating(true);
        const result = await changeUserPassword(currentPassword, newPassword);
        if (result.success) {
            showNotification('Password updated successfully!', 'success');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
        } else {
            showNotification(result.message, 'error');
        }
        setIsPasswordUpdating(false);
    };

    const handleEnable2FA = async () => {
        setIs2faLoading(true);
        const result = await generateTwoFactorSecret();
        if (result) {
            setQrCodeUrl(result.qrCodeUrl);
            setSecretKey(result.secret);
            setTwoFactorStep('setup');
        } else {
            showNotification('Could not generate 2FA secret. Please try again.', 'error');
        }
        setIs2faLoading(false);
    };
    
    const handleVerifyAndEnable2FA = async () => {
        if (verificationCode.length !== 6) {
            showNotification('Please enter a valid 6-digit code.', 'error');
            return;
        }
        setIs2faLoading(true);
        const result = await enableTwoFactor(secretKey, verificationCode);
        if (result.success && result.recoveryCodes) {
            setRecoveryCodes(result.recoveryCodes);
            setTwoFactorStep('enabled');
            showNotification('2FA enabled successfully!', 'success');
        } else {
            showNotification(result.message || 'Verification failed. The code may be incorrect.', 'error');
        }
        setIs2faLoading(false);
    };

    const handleDisable2FA = async () => {
        setIs2faLoading(true);
        const result = await disableTwoFactor(disablePassword);
        if (result.success) {
            setTwoFactorStep('idle');
            setDisablePassword('');
            showNotification('2FA disabled successfully.', 'success');
        } else {
            showNotification(result.message, 'error');
        }
        setIs2faLoading(false);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            showNotification('Copied to clipboard!', 'success');
        }).catch(() => {
            showNotification('Failed to copy.', 'error');
        });
    };
    
    if (!user) return null;

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center">
                <h1 className="text-3xl font-bold text-white">My Account</h1>
                <p className="text-slate-400 mt-1">Manage your password and account security settings.</p>
            </div>

            {/* Change Password Section */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-blue-400 mb-4 flex items-center gap-2">Change Password</h2>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                    <div className="relative">
                        <input type={showPasswords ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Current Password" required className="w-full pr-10" />
                        <button type="button" onClick={() => setShowPasswords(!showPasswords)} className="absolute inset-y-0 right-0 px-3 text-slate-400">
                           {showPasswords ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    <input type={showPasswords ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" required className="w-full" />
                    <input type={showPasswords ? "text" : "password"} value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} placeholder="Confirm New Password" required className="w-full" />
                    <button type="submit" disabled={isPasswordUpdating} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:bg-slate-500">
                        {isPasswordUpdating ? 'Updating...' : 'Update Password'}
                    </button>
                </form>
            </div>

            {/* 2FA Section */}
            <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-blue-400 mb-4">Two-Factor Authentication (2FA)</h2>
                {!user.twoFactorEnabled && twoFactorStep === 'idle' && (
                    <div className="text-center">
                        <p className="text-slate-300 mb-4">Add an extra layer of security to your account.</p>
                        <button onClick={handleEnable2FA} disabled={is2faLoading} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg">
                            {is2faLoading ? 'Generating...' : 'Enable 2FA'}
                        </button>
                    </div>
                )}

                {twoFactorStep === 'setup' && (
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                        <div className="text-center">
                            <p className="font-semibold text-white mb-2">1. Scan this QR Code</p>
                            <div className="bg-white p-2 inline-block rounded-lg">
                                <img src={qrCodeUrl} alt="2FA QR Code" />
                            </div>
                        </div>
                        <div className="flex-grow">
                            <p className="text-slate-300 mb-2">Or, enter this key manually into your authenticator app (Google Authenticator, Authy, etc.):</p>
                            <div className="bg-slate-700 p-2 rounded-md text-center font-mono text-lg text-amber-300 break-all cursor-pointer" onClick={() => copyToClipboard(secretKey)}>
                                {secretKey}
                            </div>
                            <p className="font-semibold text-white mt-6 mb-2">2. Verify & Enable</p>
                            <p className="text-slate-400 text-sm mb-2">Enter the 6-digit code from your app to complete setup.</p>
                            <div className="flex items-center gap-2">
                                <input 
                                    value={verificationCode} 
                                    onChange={e => setVerificationCode(e.target.value)} 
                                    placeholder="6-digit code" 
                                    maxLength={6}
                                    className="flex-grow"
                                />
                                <button onClick={handleVerifyAndEnable2FA} disabled={is2faLoading} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg">
                                    {is2faLoading ? 'Verifying...' : 'Verify'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {twoFactorStep === 'enabled' && (
                    <div className="text-center">
                        <h3 className="text-lg font-bold text-green-400 mb-2">2FA Enabled Successfully!</h3>
                        <p className="text-slate-300 mb-4">Please save these recovery codes in a safe place. They can be used to access your account if you lose your 2FA device.</p>
                        <div className="bg-slate-700 p-4 rounded-lg space-y-2 max-w-md mx-auto">
                            {recoveryCodes.map(code => (
                                <div key={code} className="font-mono text-lg text-amber-300 tracking-widest">{code}</div>
                            ))}
                        </div>
                        <button onClick={() => setTwoFactorStep('idle')} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg">
                            Done
                        </button>
                    </div>
                )}

                {user.twoFactorEnabled && twoFactorStep === 'idle' && (
                     <div>
                        <p className="text-green-400 mb-4">Two-Factor Authentication is currently enabled on your account.</p>
                        <form onSubmit={(e) => { e.preventDefault(); handleDisable2FA(); }} className="max-w-sm space-y-3">
                             <input 
                                type="password" 
                                value={disablePassword} 
                                onChange={e => setDisablePassword(e.target.value)} 
                                placeholder="Enter current password to disable" 
                                className="w-full"
                                required
                            />
                            <button type="submit" disabled={is2faLoading} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">
                                {is2faLoading ? 'Disabling...' : 'Disable 2FA'}
                            </button>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyAccountScreen;